import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useUpcomingEvents, usePastEvents } from '@/hooks/useEvents';
import { useCategoriesByType } from '@/hooks/useCategories';
import { Calendar, MapPin, Clock } from 'lucide-react';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import PageContainer from '@/components/PageContainer';
import PageHeader from '@/components/PageHeader';
import ContentSection from '@/components/ContentSection';
import { extractPlainText } from '@/lib/lexical-parser';
import type { Event } from '@/types';

export default function EventsPage() {
  const { language } = useAppLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch categories dynamically from API
  const { data: categories = [] } = useCategoriesByType(language, 'events');

  const { data: upcomingEvents = [], isLoading: upcomingLoading } = useUpcomingEvents(language);
  const { data: pastEvents = [], isLoading: pastLoading } = usePastEvents(language);

  const allEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;
  const isLoading = activeTab === 'upcoming' ? upcomingLoading : pastLoading;

  const filteredEvents = useMemo(() => {
    return selectedCategory
      ? allEvents.filter((e) => e.category === selectedCategory)
      : allEvents;
  }, [allEvents, selectedCategory]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ru' ? 'ru-RU' : language === 'pl' ? 'pl-PL' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(language === 'ru' ? 'ru-RU' : language === 'pl' ? 'pl-PL' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title={getTranslation('events', language)} />
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p>{getTranslation('loading', language) || 'Loading...'}</p>
        </ContentSection>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title={getTranslation('events', language)} />

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'upcoming' && styles.tabActive),
          }}
          onClick={() => setActiveTab('upcoming')}
        >
          {language === 'ru' ? 'Предстоящие' : language === 'pl' ? 'Nadchodzące' : 'Upcoming'}
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'past' && styles.tabActive),
          }}
          onClick={() => setActiveTab('past')}
        >
          {language === 'ru' ? 'Архив' : language === 'pl' ? 'Archiwum' : 'Archive'}
        </button>
      </div>

      {/* Category Filter */}
      <div style={styles.categoriesContainer}>
        <button
          style={{
            ...styles.categoryBadge,
            ...(selectedCategory === null && styles.categoryBadgeActive),
          }}
          onClick={() => setSelectedCategory(null)}
        >
          {language === 'ru' ? 'Все' : language === 'pl' ? 'Wszystkie' : 'All'}
        </button>
        {categories.map((category) => (
          <button
            key={category.slug}
            style={{
              ...styles.categoryBadge,
              ...(selectedCategory === category.slug && styles.categoryBadgeActive),
            }}
            onClick={() => setSelectedCategory(category.slug)}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Events List */}
      {filteredEvents.length > 0 ? (
        <div style={styles.eventsList}>
          {filteredEvents.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              formatDate={formatDate}
              formatTime={formatTime}
              onNavigate={() => navigate(`/events/${event.id}`, { state: { fromApp: true } })}
            />
          ))}
        </div>
      ) : (
        <div style={styles.emptyState}>
          <Calendar size={64} color={Colors.border} style={{ marginBottom: '16px' }} />
          <h2 style={styles.emptyTitle}>
            {activeTab === 'upcoming'
              ? language === 'ru'
                ? 'Нет предстоящих событий'
                : language === 'pl'
                ? 'Brak nadchodzących wydarzeń'
                : 'No upcoming events'
              : language === 'ru'
              ? 'Нет событий в архиве'
              : language === 'pl'
              ? 'Brak wydarzeń w archiwum'
              : 'No events in archive'}
          </h2>
        </div>
      )}

      <div style={{ height: '40px' }} />
    </PageContainer>
  );
}

interface EventCardProps {
  event: Event;
  formatDate: (dateString: string) => string;
  formatTime: (dateString: string) => string;
  onNavigate: () => void;
}

function EventCard({ event, formatDate, formatTime, onNavigate }: EventCardProps) {
  return (
    <div style={styles.eventCard} onClick={onNavigate}>
      {event.imageUrl && (
        <img src={event.imageUrl} alt={event.title} style={styles.eventImage} />
      )}
      <div style={styles.eventContent}>
        <div style={styles.eventHeader}>
          <h3 style={styles.eventTitle}>{event.title}</h3>
          {event.featured && <span style={styles.featuredBadge}>★</span>}
        </div>

        <p style={styles.eventDescription}>{extractPlainText(event.description || '')}</p>

        <div style={styles.eventMeta}>
          <div style={styles.metaItem}>
            <Clock size={14} color={Colors.textSecondary} style={{ marginRight: '6px' }} />
            <span style={styles.metaText}>{formatDate(event.startDate)} • {formatTime(event.startDate)}</span>
          </div>

          {event.location && (
            <div style={styles.metaItem}>
              <MapPin size={14} color={Colors.textSecondary} style={{ marginRight: '6px' }} />
              <span style={styles.metaText}>{event.location}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  tabContainer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 16px 0',
    borderBottom: `1px solid ${Colors.border}`,
  } as React.CSSProperties,

  tab: {
    padding: '12px 16px',
    border: 'none',
    background: 'transparent',
    color: Colors.textSecondary,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    borderBottom: `2px solid transparent`,
    transition: 'all 0.2s',
  } as React.CSSProperties,

  tabActive: {
    color: Colors.text,
    borderBottom: `2px solid ${Colors.primary}`,
  } as React.CSSProperties,

  categoriesContainer: {
    display: 'flex',
    gap: '8px',
    padding: '16px',
    overflowX: 'auto' as const,
    scrollBehavior: 'smooth' as const,
  } as React.CSSProperties,

  categoryBadge: {
    padding: '8px 12px',
    border: `1px solid ${Colors.border}`,
    borderRadius: '16px',
    background: Colors.surface,
    color: Colors.textSecondary,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.2s',
  } as React.CSSProperties,

  categoryBadgeActive: {
    background: Colors.primary,
    color: Colors.surface,
    borderColor: Colors.primary,
  } as React.CSSProperties,

  eventsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    padding: '16px',
  } as React.CSSProperties,

  eventCard: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    borderRadius: '12px',
    background: Colors.surface,
    border: `1px solid ${Colors.border}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
  } as React.CSSProperties,

  eventImage: {
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    objectFit: 'cover' as const,
    flexShrink: 0,
  } as React.CSSProperties,

  eventContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'space-between',
  } as React.CSSProperties,

  eventHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '6px',
  } as React.CSSProperties,

  eventTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: Colors.text,
    margin: 0,
    flex: 1,
  } as React.CSSProperties,

  featuredBadge: {
    fontSize: '16px',
    color: Colors.accent,
    flexShrink: 0,
  } as React.CSSProperties,

  eventDescription: {
    fontSize: '13px',
    color: Colors.textSecondary,
    margin: '0 0 8px 0',
    display: '-webkit-box' as const,
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
  } as React.CSSProperties,

  eventMeta: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  } as React.CSSProperties,

  metaItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  metaText: {
    fontSize: '12px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 24px',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: Colors.text,
    margin: 0,
  } as React.CSSProperties,
};
